/**
 * Curated topic set + box constants for the zero-commitment demo game (#246, FR-17).
 *
 * A logged-out visitor picks 3 of these on the landing demo picker. Every entry is a
 * VERBATIM member of {@link CURATED_CATEGORY_SEEDS} — the categories the offline batch
 * (`generate-clue-store.ts`) pre-generates — so a demo board always assembles fully from
 * the offline clue store with **zero live generation** (FR-17.2 / NFR-6.5): no anonymous
 * Bedrock spend, no abuse surface. A test in this package asserts the ⊆-seeds invariant;
 * keep it holding whenever this list changes.
 *
 * Lives in `shared` (not `web`/`game-server`) as a plain constant — no wire type, no
 * `PROTOCOL_VERSION` impact — so the picker UI, the demo create endpoint's server-side
 * validation, and the invariant test all agree on one source of truth.
 */

import { CURATED_CATEGORY_SEEDS } from './clue-store-seeds';

/**
 * The topics offered on the demo picker (D246.2). A hand-picked, broadly-appealing
 * subset of the store-covered seeds — chosen for cold traffic (pop culture + evergreen
 * general knowledge). Deterministic order (the picker renders them as drawn).
 */
export const CURATED_DEMO_TOPICS: readonly string[] = [
  'Movies',
  'World Capitals',
  'Space & Astronomy',
  'Video Games',
  'Animals',
  'Famous Landmarks',
  'Sports',
  'Dinosaurs',
  'Mythology',
  'Television',
  'Food & Cooking',
  'Comic Book Heroes',
];

/** How many topics the visitor picks for a demo board (the board has 6 categories; the
 *  generator fills the remaining columns from adjacent store-covered categories). */
export const DEMO_TOPIC_PICK_COUNT = 3;

/**
 * Demo clue ceiling (FR-17.4): a safety upper bound = exactly one full round
 * (6 categories × 5 = 30 clues). The **time box below is the practical bound** (a real
 * visitor won't play 30 clues in the box window); this ceiling exists so a fast player
 * can't exhaust round one and auto-advance the demo into the DOUBLE round / Final Peril —
 * those are deliberately reserved as full-game unlocks (the demo is a round-one taste).
 * Reaching it ends the demo → the convert screen.
 */
export const DEMO_CLUE_CAP = 30;

/**
 * Demo time box (FR-17.4), seconds — the PRACTICAL bound on a demo. A wall-clock deadline
 * set at creation; when it elapses the demo auto-ends to `GAME_OVER` → the convert CTA,
 * regardless of phase, so it also bounds an abandoned demo's lifetime (aggressive
 * teardown, D246.6). Surfaced as a visible countdown in the demo ribbon.
 *
 * 5 minutes — a full, unhurried round-one taste. (Overridable per-environment via the
 * server's `demoTimeBoxSec` option so CI / deployed e2e can run a short box.)
 */
export const DEMO_TIME_BOX_SEC = 300;

const DEMO_TOPIC_SET: ReadonlySet<string> = new Set(CURATED_DEMO_TOPICS);
const SEED_SET: ReadonlySet<string> = new Set(CURATED_CATEGORY_SEEDS);

/** True when `topic` is one of the curated demo topics (server-side create validation). */
export function isCuratedDemoTopic(topic: string): boolean {
  return DEMO_TOPIC_SET.has(topic);
}

/**
 * Validate a demo topic selection server-side (FR-17.2): exactly
 * {@link DEMO_TOPIC_PICK_COUNT} distinct topics, each a curated (store-covered) topic.
 * Returns the de-duplicated selection, or `null` if invalid — the caller rejects the
 * create rather than risk a live-generation fallback on an anonymous request.
 */
export function validateDemoTopics(input: unknown): string[] | null {
  if (!Array.isArray(input)) return null;
  const seen = new Set<string>();
  for (const t of input) {
    if (typeof t !== 'string' || !isCuratedDemoTopic(t)) return null;
    seen.add(t);
  }
  if (seen.size !== DEMO_TOPIC_PICK_COUNT) return null;
  return [...seen];
}

/** Every curated demo topic must be a store-covered seed (the zero-LLM guarantee). */
export function allDemoTopicsAreSeeded(): boolean {
  return CURATED_DEMO_TOPICS.every((t) => SEED_SET.has(t));
}
